import { useState } from 'react'
import {
  SectionCard, Button, Checkbox, DataTable, Modal, Textarea, type Column,
  type, textColor, surface,
} from '@datahub/design-system'
import type { SectionDef, FieldDef } from '@datahub/shared'
import { isFieldRequired, isFieldVisible, visibleSectionFields, allSectionFields } from '@datahub/shared'
import { FieldRenderer } from './FieldRenderer'
import { RowDrawer } from './RowDrawer'
import { StatusTag } from '../../components/StatusTag'
import type { DraftRow, TabState, ValidationIssue } from '../../lib/types'
import type { PatchOperation } from '@datahub/shared'

interface SectionRendererProps {
  tab: string
  section: SectionDef
  state: TabState
  profile: Record<string, unknown>
  issues: ValidationIssue[]
  readOnly?: boolean
  push: (...ops: PatchOperation[]) => void
}

function newRowId(): string {
  return crypto.randomUUID()
}

function rowLabel(section: SectionDef, row: DraftRow): string {
  const template = section.rowLabel ?? '{name}'
  const label = template.replace(/\{([^}]+)\}/g, (_, key: string) => {
    const v = row.values[key] ?? row.values[`datahub.${key}`]
    if (Array.isArray(v)) return v.map((x) => (typeof x === 'object' && x && 'name' in x ? String((x as { name: string }).name) : String(x))).join(', ')
    if (v && typeof v === 'object' && 'value' in (v as object)) return String((v as { value: string }).value)
    return v === undefined || v === null ? '' : String(v)
  })
  return label.replace(/\s*—\s*$/, '').replace(/^\s*—\s*/, '').trim() || 'Untitled entry'
}

/** A row Stears supplied — from a CSV pre-load or from its own records — for the company to confirm. */
function isFromStears(row: DraftRow): boolean {
  return Boolean(row.preloadedRowId || row.providedByStears)
}

/** Inputs that do not fit a single grid column and take the full row instead. */
const WIDE_INPUTS = new Set(['longtext', 'partylist', 'taglist', 'multiselect'])

/** Renders one section: plain fields, a repeatable table, or the uploads list. */
export function SectionRenderer({ tab, section, state, profile, issues, readOnly, push }: SectionRendererProps) {
  const [drawerRow, setDrawerRow] = useState<DraftRow | null>(null)
  const [confirmRemove, setConfirmRemove] = useState<DraftRow | null>(null)
  const [removeReason, setRemoveReason] = useState('')

  const toggleKey = section.toggle?.key
  const toggled = toggleKey ? Boolean(state.flags[toggleKey]) : false
  const rows = state.rows[section.key] ?? []

  const scalarCtx = {
    values: Object.fromEntries(
      Object.entries(state.values).map(([k, v]) => [k.includes('.') ? k.slice(k.indexOf('.') + 1) : k, v]),
    ),
    controls: state.controls,
    profile,
  }

  const issueFor = (field: FieldDef) =>
    issues.find((i) => i.section === section.key && !i.rowId && i.field === field.key && i.severity === 'error')?.message

  /* ------------------------------------------------------------ repeatable */
  if (section.kind === 'repeatable') {
    const columns: Column<DraftRow>[] = [
      {
        key: 'label', header: section.label, width: 380, sticky: true,
        render: (r) => (
          <span className="flex items-center" style={{ gap: 8 }}>
            <span>{rowLabel(section, r)}</span>
            {r.confirmationState === 'confirmed' && isFromStears(r) && <StatusTag status="pending" />}
          </span>
        ),
      },
      {
        key: 'state', header: 'Status', width: 150,
        render: (r) =>
          isFromStears(r) && r.confirmationState === 'confirmed'
            ? 'Provided by Stears'
            : r.confirmationState === 'edited'
              ? 'Edited'
              : 'Added by you',
      },
      ...(section.confidentiality === 'row'
        ? [{
            key: 'confidential', header: 'Confidential', width: 130,
            render: (r: DraftRow) => (r.confidential || String(r.values['datahub.publication_permission'] ?? '').startsWith('Confidential') ? <StatusTag status="confidential" /> : '-'),
          } as Column<DraftRow>]
        : []),
      {
        key: 'actions', header: '', width: 170,
        render: (r) => (
          <span className="flex items-center" style={{ gap: 12 }}>
            <button
              type="button" disabled={readOnly}
              onClick={() => setDrawerRow(r)}
              className="text-text-hyperlink hover:underline"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0 }}
            >
              {isFromStears(r) && r.confirmationState === 'confirmed' ? 'Review' : 'Edit'}
            </button>
            <button
              type="button" disabled={readOnly}
              onClick={() => { setConfirmRemove(r); setRemoveReason('') }}
              className="text-text-support hover:text-white"
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, padding: 0 }}
            >
              Remove
            </button>
          </span>
        ),
      },
    ]

    return (
      <>
        <SectionCard title={section.label}>
          <div className="flex flex-col">
            {section.help && (
              <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '22px', padding: '12px 20px 0' }}>
                {section.help}
              </p>
            )}
            {section.toggle && (
              <div style={{ padding: '12px 20px' }}>
                <Checkbox
                  checked={toggled}
                  disabled={readOnly}
                  onChange={(v) => push({ op: 'flag', tab, key: section.toggle!.key, value: v } as PatchOperation)}
                  label={section.toggle.label}
                />
              </div>
            )}
            {!toggled && (
              <>
                <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} caption={section.label} emptyMessage={`No ${section.label.toLowerCase()} recorded yet`} />
                {!readOnly && (
                  <div style={{ padding: '12px 20px' }}>
                    <Button
                      variant="outline"
                      size="small"
                      onClick={() => setDrawerRow({ id: newRowId(), values: {}, confirmationState: 'new' })}
                    >
                      Add {section.label.replace(/s$/, '').toLowerCase()}
                    </Button>
                  </div>
                )}
              </>
            )}
          </div>
        </SectionCard>

        <RowDrawer
          section={section}
          open={drawerRow !== null}
          row={drawerRow}
          profile={profile}
          controls={state.controls}
          issues={issues}
          onClose={() => setDrawerRow(null)}
          onSave={(row) => {
            push({ op: 'rowUpsert', tab, section: section.key, row } as PatchOperation)
            setDrawerRow(null)
          }}
        />

        <Modal
          open={confirmRemove !== null}
          onClose={() => setConfirmRemove(null)}
          variant="destructive"
          title={`Remove ${confirmRemove ? rowLabel(section, confirmRemove) : ''}?`}
          primaryLabel="Remove"
          primaryDisabled={Boolean(confirmRemove && isFromStears(confirmRemove)) && removeReason.trim().length < 3}
          onPrimary={() => {
            if (!confirmRemove) return
            push({
              op: 'rowRemove', tab, section: section.key, rowId: confirmRemove.id,
              ...(removeReason.trim() ? { reason: removeReason.trim() } : {}),
            } as PatchOperation)
            setConfirmRemove(null)
          }}
        >
          <div className="flex flex-col" style={{ gap: 16 }}>
            <p className="font-mulish text-white" style={{ fontSize: 12, lineHeight: '22px' }}>
              {confirmRemove && isFromStears(confirmRemove)
                ? 'Stears provided this entry. Tell us why it should be removed; the reason is added to this tab’s notes.'
                : 'This entry will be removed from your submission.'}
            </p>
            <Textarea
              label="Reason"
              value={removeReason}
              onChange={setRemoveReason}
              rows={3}
              maxLength={500}
              {...(confirmRemove && isFromStears(confirmRemove) ? {} : { help: 'Optional' })}
            />
          </div>
        </Modal>
      </>
    )
  }

  /* ------------------------------------------------------------ plain fields */
  const fields = visibleSectionFields(section, scalarCtx)
  const hidden = allSectionFields(section).length - fields.length

  /**
   * Figma "Portfolio Reporting" (node 4352:18988): a section is a 14px heading over a
   * three-column grid of 355px fields, closed by a hairline rule. Wide inputs such as
   * long text and repeatable party lists span the full row so they stay usable.
   */
  return (
    <section className="flex flex-col" style={{ gap: 20, paddingBottom: 20 }}>
      <h3 className="font-jakarta" style={{ ...type.sectionHeading, fontWeight: 500, color: textColor.primary }}>
        {section.label}
      </h3>

      {section.help && (
        <p className="font-jakarta" style={{ ...type.body, color: textColor.faint }}>{section.help}</p>
      )}

      {section.toggle && (
        <Checkbox
          checked={toggled}
          disabled={readOnly}
          onChange={(v) => push({ op: 'flag', tab, key: section.toggle!.key, value: v } as PatchOperation)}
          label={section.toggle.label}
        />
      )}

      {!toggled && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            columnGap: 20,
            rowGap: 20,
            alignItems: 'start',
          }}
        >
          {fields.map((field) => (
            <div
              key={field.key}
              style={WIDE_INPUTS.has(field.input) ? { gridColumn: '1 / -1' } : undefined}
            >
              <FieldRenderer
                field={{ ...field, label: isFieldRequired(field, scalarCtx) ? `${field.label} *` : field.label }}
                value={state.values[`${section.key}.${field.key}`]}
                onChange={(value) =>
                  push({ op: 'set', tab, section: section.key, field: field.key, value } as PatchOperation)
                }
                {...(issueFor(field) ? { error: issueFor(field)! } : {})}
                siblings={scalarCtx.values}
                disabled={readOnly}
              />
            </div>
          ))}
        </div>
      )}

      {!toggled && hidden > 0 && (
        <p className="font-jakarta" style={{ ...type.eyebrow, color: textColor.faint }}>
          {hidden} further field{hidden === 1 ? '' : 's'} will appear once related answers are given.
        </p>
      )}

      <div style={{ height: 0, borderTop: `0.5px solid ${surface.border}` }} />
    </section>
  )
}

export { isFieldVisible }
